import { useEffect, useState } from 'react';
import { addListener, AsyncThunk } from '@reduxjs/toolkit';
import { RootState, useAppDispatch } from '../store/Store';


/**
 * Capture async thunk's lifecycle events. This is useful for one shot thunks like POSTing updates where you dont really
 * want to add variables in a slice to track the execution of the thunk.
 * 
 * @param asyncThunk Async thunk you want to listen to
 * @param onPending Called whenever the thunk switches to pending.
 * @param onFulfilled Called whenever the thunk switches to fullfilled.
 * @param onRejected Called whenever the thunk switches to rejected.
 * 
 * @returns lastStatus 
 * 	Status is the last observed status.
 * 	Status does not draw a distinction between the same thunk called with different args. 
 *  Use the callbacks for precise control.
 *  Note that the return object itself is not stable -- will be a new object every render.
 */
export const useAsyncThunkListener = <Returned, ThunkArg, ThunkApiConfig>(
	asyncThunk: AsyncThunk<Returned,ThunkArg,ThunkApiConfig>,
	onPending?: (action: ReturnType<typeof asyncThunk.pending>, state: RootState) => void,
	onFulfilled?: (action: ReturnType<typeof asyncThunk.fulfilled>, state: RootState) => void,
	onRejected?: (action: ReturnType<typeof asyncThunk.rejected>, state: RootState) => void,
) => 
{
	const dispatch = useAppDispatch();

	const [lastStatus, setLastStatus] = useState<{
		status: 'none',
	} | {
		status:  'pending',
		action: ReturnType<typeof asyncThunk.pending>,
	} | {
		status: 'fulfilled',
		action: ReturnType<typeof asyncThunk.fulfilled>,
	} | {
		status: 'rejected',
		action: ReturnType<typeof asyncThunk.rejected>,
	}>({ status: 'none' });

	/** Reset last status back to its initial state. */
	const clearLastStatus = () => setLastStatus({ status: 'none' });

	useEffect(() => 
	{
		const unsubPending = dispatch(
			addListener({
				actionCreator: asyncThunk.pending,
				effect: (action, listenerApi) => 
				{
					setLastStatus({ status: 'pending', action, });

					if (onPending) 
						onPending(action, listenerApi.getState() as RootState);
				},
			}),
		) as unknown as Function;
		const unsubFullfilled = dispatch(
			addListener({
				actionCreator: asyncThunk.fulfilled,
				effect: (action, listenerApi) => 
				{
					setLastStatus({ status: 'fulfilled', action, });

					if (onFulfilled) 
						onFulfilled(action, listenerApi.getState() as RootState);
				},
			}),
		) as unknown as Function; // Redux types are wrong for some reason.
		const unsubRejected = dispatch(
			addListener({
				actionCreator: asyncThunk.rejected,
				effect: (action, listenerApi) => 
				{
					setLastStatus({ status: 'rejected', action, });

					if (onRejected) 
						onRejected(action, listenerApi.getState() as RootState);
				},
			}),
		) as unknown as Function;
		return () => 
		{
			try { unsubFullfilled(); }
			catch (e) { console.log('Error unsubbing unsubFullfilled'); }
			try { unsubPending(); }
			catch (e) { console.log('Error unsubbing unsubPending'); }
			try { unsubRejected(); }
			catch (e) { console.log('Error unsubbing unsubRejected'); }
		};
	}, []);

	return {
		...lastStatus,
		clearLastStatus,
		// For consistency and convenience.
		isLoading: lastStatus.status === 'pending',
		isError: lastStatus.status === 'rejected',
		errorMessage: lastStatus.status === 'rejected' 
			? ('string' === typeof lastStatus.action.payload) 
				? lastStatus.action.payload 
				: undefined
			: undefined,
		data: lastStatus.status === 'fulfilled' 
			? lastStatus.action.payload
			: undefined,
	};
};